import { FCC } from "@/types/common";
import { Stack, Typography } from "@mui/material";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { Button } from "@/components/elements/Button";
import { JobTitle } from "@/features/job/components/JobTitle";
import { TJobForm } from "@/features/job/types/JobForm";
import { JOB_STATUS } from "@/types/enum";
import { AppRoutes } from "@/routes/config";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { JOB_DELETE_CONFIRM_MESSAGE, JOB_DELETE_UNDELETABLE_MESSAGE, JOB_PAGE_STORAGE_KEY } from "@/features/job/constants/common";
import { JobStatusDetail } from "@/features/job/components/job-detail/JobStatusDetail";
import { JobBasicInfoDetail } from "@/features/job/components/job-detail/JobBasicInfoDetail";
import { JobEmploymentConditionDetail } from "@/features/job/components/job-detail/JobEmploymentConditionDetail";
import { JobWorkingConditionDetail } from "@/features/job/components/job-detail/JobWorkingConditionDetail";
import { JobRequirementDetail } from "@/features/job/components/job-detail/JobRequirementDetail";
import { JobBenefitDetail } from "@/features/job/components/job-detail/JobBenefitDetail";
import { JobFeatureDetail } from "@/features/job/components/job-detail/JobFeatureDetail";
import { JobUploadFilesDetail } from "@/features/job/components/job-detail/JobUploadFilesDetail";
import { JobCaseDetail } from "@/features/job/components/job-detail/JobCaseDetail";
import { getDynamicRoute } from "@/utils/url";
import { pickDuplicateJobData } from "@/features/job/utils/pickDuplicateJobData";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { JOB_WIDTH_FULL } from "@/features/job/constants/width";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useEffect, useRef, useState } from "react";
import { TGetJobResDto } from "@/api/services/main/job/dtos/get-job.dto";
import { QueryObserverResult } from "@tanstack/react-query";
import { IconActionDelete, IconContentCopy, IconEditor } from "@/assets/icons";
import { checkDeletableJobsApi, deleteJobsApi } from "@/api/services/main/job";
import { useDialog } from "@/hooks/useDialog";
import { toast } from "@/utils/toast";
import { isApiError } from "@/utils/error";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { queryClient } from "@/constants/queryClient";

type TJobDetailProps = {
  defaultValues?: Partial<TJobForm>;
  refetchJob: () => Promise<QueryObserverResult<TGetJobResDto | undefined>>;
  isFetching: boolean;
};

// Deep-link from case 求人情報/応募要件 tabs: scroll the detail page to the matching section.
const HASH_TO_SECTION_TITLE: Record<string, string> = { "#basic-info": "基本情報", "#requirement": "応募要件" };

export const JobDetail: FCC<TJobDetailProps> = ({ defaultValues, refetchJob, isFetching }) => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { hash, pathname, search } = useLocation();
  // Capture the deep-link hash once on mount — it keeps driving the auto-scroll below even after
  // the hash is stripped from the URL.
  const deepLinkHashRef = useRef(hash);
  const scrollToTitle = !isFetching ? HASH_TO_SECTION_TITLE[deepLinkHashRef.current] : undefined;

  // Keep the URL clean: once the deep-link hash is captured, strip it (e.g. /job/1/detail#basic-info
  // → /job/1/detail). After stripping, hash is "" so this is a no-op on later renders.
  useEffect(() => {
    if (HASH_TO_SECTION_TITLE[hash]) navigate(pathname + search, { replace: true });
  }, [hash, pathname, search, navigate]);
  const [showConfirmEditArchiveCompany, setShowConfirmEditArchiveCompany] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeletable, setIsDeletable] = useState(true);
  const confirmDelete = useDialog();

  const isDraft = defaultValues?.status === JOB_STATUS.DRAFT;
  const getBackUrl = () => {
    const savedPage = sessionStorage.getItem(JOB_PAGE_STORAGE_KEY) || "1";
    return `${AppRoutes.jobList}?page=${savedPage}`;
  };
  const handleBackToList = () => {
    const backUrl = getBackUrl();
    navigate(backUrl, { state: { preserveSelection: true } });
  };

  const handleEditClick = async () => {
    const result = await refetchJob();
    if (result && result?.data?.data?.company?.isArchive) {
      setShowConfirmEditArchiveCompany(true);
    } else {
      toEditRoute();
    }
  };

  const toEditRoute = () => {
    const jobEditRoute = getDynamicRoute(AppRoutes.jobEdit, { jobId: jobId });
    navigate(jobEditRoute);
  };

  const handleDuplicateJob = (defaultValues?: Partial<TJobForm>) => {
    if (defaultValues) {
      const key = "duplicated-job-data";
      const duplicatedJobData = pickDuplicateJobData(defaultValues);
      localStorage.setItem(key, JSON.stringify(duplicatedJobData));
    }

    const jobCreateRoute = getDynamicRoute(AppRoutes.jobCreate, {});
    window.open(jobCreateRoute, "_blank");
  };

  const handleDeleteClick = async () => {
    if (!jobId || isDeleting) return;

    try {
      setIsDeleting(true);
      const { data: deletableMap } = await checkDeletableJobsApi([Number(jobId)]);
      const canDelete = deletableMap[Number(jobId)];
      setIsDeletable(canDelete);
      setIsDeleting(false);

      const confirmed = await confirmDelete.show();
      if (!confirmed || !canDelete) return;

      setIsDeleting(true);
      const rs = await deleteJobsApi([Number(jobId)]);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB.FETCH_LIST] });
      navigate(getBackUrl());
    } catch (error: unknown) {
      if (isApiError(error)) {
        toast.error(error.message);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <JobTitle
        title={defaultValues?.jobName}
        tagIds={defaultValues?.tagIds}
        status={defaultValues?.status}
        createdAt={defaultValues?.createdAt}
        updatedAt={defaultValues?.updatedAt}
        shareLinks={defaultValues?.shareLinks}
        onShareGenerated={() => refetchJob()}
        showShareButton
        isCompanyArchived={defaultValues?.company?.isArchive}
      />
      <Stack mt={1} width={`${JOB_WIDTH_FULL}px`}>
        <MenuNavigate
          contents={[
            { title: "ステータス状況", content: <JobStatusDetail defaultValues={defaultValues} isDraft={isDraft} /> },
            { title: "案件参加状況", content: <JobCaseDetail defaultValues={defaultValues} /> },
            { title: "基本情報", content: <JobBasicInfoDetail defaultValues={defaultValues} isDraft={isDraft} /> },
            { title: "雇用条件", content: <JobEmploymentConditionDetail defaultValues={defaultValues} isDraft={isDraft} /> },
            { title: "勤務条件", content: <JobWorkingConditionDetail defaultValues={defaultValues} isDraft={isDraft} /> },
            { title: "応募要件", content: <JobRequirementDetail defaultValues={defaultValues} isDraft={isDraft} /> },
            { title: "福利厚生", content: <JobBenefitDetail defaultValues={defaultValues} /> },
            { title: "求人特徴", content: <JobFeatureDetail defaultValues={defaultValues} /> },
            { title: "その他関連情報", content: <JobUploadFilesDetail defaultValues={defaultValues} /> },
          ]}
          contentWidth="900px"
          scrollToTitle={scrollToTitle}
          contentAffix={
            <FooterContainer>
              <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                <Button variant="ghost" size="sm" onClick={handleBackToList}>
                  戻る
                </Button>
                <Stack direction="row" gap={2}>
                  <Button variant="danger" size="sm" onClick={handleDeleteClick} disabled={isDeleting || isFetching} startIcon={<IconActionDelete />}>
                    削除
                  </Button>
                  <Button type="submit" variant="outline" size="sm" onClick={handleEditClick} disabled={isFetching} startIcon={<IconEditor />}>
                    編集
                  </Button>
                  <Button type="submit" variant="primary" size="sm" onClick={() => handleDuplicateJob(defaultValues)} startIcon={<IconContentCopy />}>
                    求人を複製
                  </Button>
                </Stack>
              </Stack>
            </FooterContainer>
          }
        />
      </Stack>

      <ConfirmSaveModal open={showConfirmEditArchiveCompany} onClose={() => setShowConfirmEditArchiveCompany(false)} onOk={() => toEditRoute()}>
        <Typography>アーカイブ済み企業の求人を編集しようとしています。編集を続行してもよろしいですか？</Typography>
      </ConfirmSaveModal>

      <ConfirmDeleteModal
        open={confirmDelete.open}
        onOk={confirmDelete.confirm}
        onClose={confirmDelete.cancel}
        message={isDeletable ? JOB_DELETE_CONFIRM_MESSAGE : JOB_DELETE_UNDELETABLE_MESSAGE}
        hiddenOkBtn={!isDeletable}
        labelClose={!isDeletable ? "閉じる" : undefined}
        justifyBtnAction={!isDeletable ? "center" : undefined}
      />
    </>
  );
};
